import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "./ui/button";
import { AlertTriangle, Zap } from "lucide-react";

/**
 * Shows an upgrade nudge when company usage crosses 80% of the monthly limit.
 * Hidden once the company is on the "standard" plan and below the soft warning threshold.
 */
export default function UsageBanner() {
  const [stats, setStats] = useState(null);
  const [plan, setPlan] = useState(null);
  const [dismissed, setDismissed] = useState(
    typeof window !== "undefined" && sessionStorage.getItem("ren_usage_dismissed") === "1"
  );

  useEffect(() => {
    api.get("/usage").then(r => setStats(r.data)).catch(() => {});
    api.get("/company").then(r => setPlan(r.data?.company?.subscription_plan)).catch(() => {});
  }, []);

  if (dismissed || !stats) return null;
  const pct = stats.usage_percent ?? 0;
  if (pct < 80) return null;

  const blocked = pct >= 100;
  const onStandard = plan === "standard";

  // If user is on standard AND under hard cap we still warn but with softer copy
  const tone = blocked
    ? "bg-destructive/10 border-destructive/30 text-destructive-foreground"
    : "bg-accent/15 border-accent/40";

  return (
    <div
      data-testid="usage-banner"
      className={`flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border px-4 py-3 mb-6 ${tone}`}
    >
      <div className="flex items-start gap-3 flex-1">
        {blocked ? <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0 text-destructive" />
                 : <Zap className="w-5 h-5 mt-0.5 flex-shrink-0 text-accent" />}
        <div className="text-sm">
          <div className="font-medium">
            {blocked
              ? "You've hit your monthly usage limit."
              : `You've used ${pct}% of your monthly usage.`}
          </div>
          <div className="text-muted-foreground mt-0.5">
            {blocked
              ? "New chatbot replies are throttled. Upgrade to keep your assistant answering customers."
              : onStandard
                ? "You're on Standard. Consider scheduling a usage review or contact support before you hit the cap."
                : "Upgrade to Standard before you run out — your customers depend on instant answers."}
          </div>
        </div>
      </div>
      <div className="flex gap-2 sm:flex-shrink-0">
        {!onStandard && (
          <Link to="/dashboard/subscription">
            <Button size="sm" data-testid="usage-upgrade-btn" className="bg-accent text-accent-foreground hover:bg-accent/90">
              Upgrade
            </Button>
          </Link>
        )}
        <Button
          size="sm"
          variant="ghost"
          data-testid="usage-dismiss-btn"
          onClick={() => { sessionStorage.setItem("ren_usage_dismissed", "1"); setDismissed(true); }}
        >
          Dismiss
        </Button>
      </div>
    </div>
  );
}
